import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ForceGraph2DImport from 'react-force-graph-2d';

const ForceGraph2D = ForceGraph2DImport as React.ComponentType<
  Record<string, unknown>
>;
import { X } from 'lucide-react';
import { getFileContent } from '@/components/smartai/api/getFileContent';
import {
  useCallGraph,
  type ScanCallableEntity,
  type EntityMetadata,
} from '@/components/smartai/api/getCallGraph';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import CodeEditor from '@/components/common/CodeEditor';
import { cn } from '@/lib/utils';
import {
  callGraphRootClass,
  callGraphToolbarClass,
  callGraphStatChipClass,
  callGraphStatLabelClass,
  callGraphStatValueClass,
  callGraphToolbarHintClass,
  callGraphLayoutClass,
  callGraphCanvasClass,
  callGraphLegendClass,
  callGraphSearchWrapClass,
  callGraphSearchFieldClass,
  callGraphSearchInputClass,
  callGraphSearchClearClass,
  callGraphSearchResultsClass,
  callGraphSearchCountClass,
  callGraphSearchEmptyClass,
  callGraphSearchListClass,
  callGraphSearchItemClass,
  callGraphSearchItemLabelClass,
  callGraphSearchItemPathClass,
  callGraphDetailsPanelClass,
  callGraphDetailsHeaderClass,
  callGraphDetailsBodyClass,
  callGraphCallablesSectionClass,
  callGraphSourceSectionClass,
  callGraphSourceTitleClass,
  callGraphSourceScrollClass,
  callGraphCallableCardClass,
  callGraphScoreGridClass,
  callGraphScoreChipClass,
  callGraphBlastRadiusSectionClass,
  callGraphDepthSelectClass,
} from './call-graph-layout';
import {
  CallGraphSourceSkeleton,
  CallGraphVisualizationSkeleton,
} from './CallGraphSkeleton';
import {
  computeRiskHeatmap,
  RISK_HEATMAP_LEGEND,
} from './call-graph-risk-heatmap';
import {
  blastRadiusDepthOptions,
  buildEntityKeyToNodeIds,
  buildFilePathToNodeId,
  computeBlastRadius,
  formatBlastRadiusDepthLabel,
  type BlastRadiusResult,
} from './call-graph-blast-radius';

type Props = {
  scanId: string;
  repoUrl?: string;
  branchName?: string;
  patToken?: string;
  onBack?: () => void;
  /** When true, hides page header (used inside Test Optimization Graph tab). */
  embedded?: boolean;
  /** Pre-loaded graph from parent — avoids duplicate /api/graph/call-graph calls. */
  graphPayload?: import('@/components/smartai/api/getCallGraph').CallGraphApiResponse;
  graphLoading?: boolean;
  graphError?: unknown;
};

type FileDetailState = {
  filePath: string;
  content: string;
  language?: string;
  loadingCode: boolean;
};

// ─── one fixed color per entity type ─────────────────────────────────────────
const TYPE_COLOR: Record<string, string> = {
  File: '#06B6D4',
  Function: '#22C55E',
  Method: '#3B82F6',
  Constructor: '#A855F7',
};
const FALLBACK_COLOR = '#94A3B8';

const EDGE_COLOR = {
  calls: '#FCD34D',
  api_calls: '#34D399',
  contains: '#93C5FD',
} as const;

const EDGE_LEGEND = [
  { color: EDGE_COLOR.calls, label: 'Call', dashed: false },
  { color: EDGE_COLOR.api_calls, label: 'API (FE→BE)', dashed: true },
  { color: EDGE_COLOR.contains, label: 'Contains', dashed: false },
] as const;

function getEntityRowId(ent?: ScanCallableEntity): string {
  if (!ent) return '';
  const entityId = (ent.entityId ?? '').toString().trim();
  if (entityId) return entityId;
  const docId = (ent._id ?? '').toString().trim();
  if (docId) return docId;
  const ek = (ent.entityKey ?? '').toString().trim();
  const line = ent.lineStart;
  if (ek) return `${ek}:L${line ?? ''}`;
  return `${ent.type ?? 'Unknown'}:${ent.name ?? 'anonymous'}:L${line ?? ''}`;
}

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosErr = err as {
      response?: {
        data?: { detail?: unknown; message?: string; Error?: string };
      };
      message?: string;
    };
    const detail = axiosErr.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (axiosErr.response?.data?.Error)
      return String(axiosErr.response.data.Error);
    if (axiosErr.response?.data?.message) return axiosErr.response.data.message;
    if (axiosErr.message) return axiosErr.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function entityScore(ent: ScanCallableEntity): number {
  return ent.metadata?.scores?.total ?? 0;
}

function fanInList(ent: ScanCallableEntity): string[] {
  const raw = ent.metadata?.fanIn;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  return [];
}

function resolvedFanoutList(ent: ScanCallableEntity): string[] {
  const m = ent.metadata;
  if (!m) return [];
  const raw = m.resolvedFanout ?? m.resolvedFanouts ?? m.rawFanout;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  return [];
}

function buildEntitiesByKey(
  entitiesByFile: Record<string, ScanCallableEntity[]>,
): Map<string, ScanCallableEntity> {
  const map = new Map<string, ScanCallableEntity>();
  for (const list of Object.values(entitiesByFile)) {
    for (const ent of list) {
      if (ent.entityKey) map.set(ent.entityKey, ent);
    }
  }
  return map;
}

function parentNameFromEntity(ent?: ScanCallableEntity): string {
  const direct = ent?.parentName?.trim();
  if (direct) return direct;
  const parentKey = ent?.parentEntityKey?.trim();
  if (parentKey) return parentKey.split('_').pop() ?? '';
  return '';
}

function formatEntityCallableLabel(
  ent: ScanCallableEntity | undefined,
  entityKey: string,
): string {
  const name = ent?.name?.trim();
  const parent = parentNameFromEntity(ent);
  if (name) {
    if (ent?.type === 'Constructor' || name === 'constructor') {
      return parent ? `${parent}.constructor()` : 'constructor()';
    }
    if (parent && ent?.type === 'Method') return `${parent}.${name}()`;
    return `${name}()`;
  }
  const last = entityKey.split('_').pop() ?? entityKey;
  if (last === 'constructor') {
    return parent ? `${parent}.constructor()` : 'constructor()';
  }
  return last ? `${last}()` : entityKey;
}

function formatCallableLabel(
  entityKey: string,
  entitiesByKey: Map<string, ScanCallableEntity>,
): string {
  return formatEntityCallableLabel(entitiesByKey.get(entityKey), entityKey);
}

function EntityKeyListDisplay({
  label,
  keys,
  entitiesByKey,
}: {
  label: string;
  keys: string[];
  entitiesByKey: Map<string, ScanCallableEntity>;
}) {
  if (keys.length === 0) return null;
  return (
    <div className="min-w-0">
      <p className="m-0 mb-1 text-[0.625rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
        {label}
      </p>
      <div className="flex flex-wrap gap-1">
        {keys.map(k => (
          <span
            key={k}
            title={k}
            className="max-w-full truncate rounded-md border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-1.5 py-0.5 font-mono text-[0.65rem] text-[var(--inv-text-secondary)]">
            {formatCallableLabel(k, entitiesByKey)}
          </span>
        ))}
      </div>
    </div>
  );
}

const SCORE_LABEL_ORDER = [
  'fanIn',
  'fanOut',
  'leaf',
  'orphan',
  'total',
] as const;

function BlastRadiusControls({
  result,
  depth,
  maxDepth,
  maxFanInDepth,
  maxFanOutDepth,
  onDepthChange,
  onClear,
}: {
  result: BlastRadiusResult;
  depth: number;
  maxDepth: number;
  maxFanInDepth: number;
  maxFanOutDepth: number;
  onDepthChange: (depth: number) => void;
  onClear: () => void;
}) {
  const depthOptions = blastRadiusDepthOptions(maxDepth);

  return (
    <div className={callGraphBlastRadiusSectionClass}>
      <div className="flex items-start justify-between gap-2">
        <p className="m-0 text-[0.625rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
          Blast radius
        </p>
        <Button
          type="button"
          variant="ghost"
          className="h-auto p-0 text-[0.65rem] font-medium text-[var(--inv-text-muted)] hover:text-[var(--inv-text)]"
          onClick={onClear}>
          Clear
        </Button>
      </div>
      <p className="m-0 mt-1.5 text-[0.68rem] leading-snug text-[var(--inv-text-muted)]">
        Natural max:{' '}
        <span className="font-semibold text-[var(--inv-text-secondary)]">
          {maxFanInDepth}
        </span>{' '}
        fan-in ·{' '}
        <span className="font-semibold text-[var(--inv-text-secondary)]">
          {maxFanOutDepth}
        </span>{' '}
        fan-out
      </p>
      <label className="mt-1.5 block">
        <span className="sr-only">Blast radius depth</span>
        <select
          className={callGraphDepthSelectClass}
          value={depth}
          onChange={event => onDepthChange(Number(event.target.value))}
          aria-label="Blast radius depth">
          {depthOptions.map(level => (
            <option key={level} value={level}>
              {formatBlastRadiusDepthLabel(level, maxDepth)}
            </option>
          ))}
        </select>
      </label>
      <p className="m-0 mt-2 text-[0.7rem] leading-snug text-[var(--inv-text-secondary)]">
        Direct:{' '}
        <span className="font-semibold text-[var(--inv-text)]">
          {result.directFanIn}
        </span>{' '}
        fan-in ·{' '}
        <span className="font-semibold text-[var(--inv-text)]">
          {result.directFanOut}
        </span>{' '}
        fan-out
      </p>
      <p className="m-0 mt-1 text-[0.7rem] leading-snug text-[var(--inv-text-secondary)]">
        Total (depth {depth} / {maxDepth}):{' '}
        <span className="font-semibold text-[var(--inv-text)]">
          {result.totalFanIn}
        </span>{' '}
        fan-in ·{' '}
        <span className="font-semibold text-[var(--inv-text)]">
          {result.totalFanOut}
        </span>{' '}
        fan-out ·{' '}
        <span className="font-semibold text-[var(--inv-text)]">
          {result.totalAffected}
        </span>{' '}
        affected
      </p>
      {result.isLargeClosure ? (
        <p className="m-0 mt-1.5 text-[0.65rem] leading-snug text-amber-700">
          Large closure ({result.totalAffected} methods) — try a lower depth.
        </p>
      ) : null}
    </div>
  );
}

function EntityScoresDisplay({
  scores,
}: {
  scores?: EntityMetadata['scores'];
}) {
  const ordered =
    scores &&
    SCORE_LABEL_ORDER.filter(
      key => scores[key] !== undefined && scores[key] !== null,
    );
  const extra =
    scores &&
    Object.keys(scores).filter(
      k => !SCORE_LABEL_ORDER.includes(k as (typeof SCORE_LABEL_ORDER)[number]),
    );
  const keys = ordered ? [...ordered, ...(extra ?? [])] : [];

  return (
    <div className="min-w-0">
      <p className="m-0 mb-1 text-[0.625rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
        Scores
      </p>
      {keys.length === 0 ? (
        <span className="text-[0.7rem] text-[var(--inv-text-muted)]">—</span>
      ) : (
        <div className={callGraphScoreGridClass}>
          {keys.map(key => {
            const value = scores![key as keyof typeof scores];
            const isTotal = key === 'total';
            return (
              <div key={key} className={callGraphScoreChipClass}>
                <span className="truncate text-[0.625rem] uppercase tracking-wide text-[var(--inv-text-muted)]">
                  {key}
                </span>
                <span
                  className={cn(
                    'shrink-0 font-tabular text-[0.75rem]',
                    isTotal
                      ? 'font-bold text-[var(--inv-text)]'
                      : 'font-medium text-[var(--inv-text-secondary)]',
                  )}>
                  {value}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── shape drawers ───────────────────────────────────────────────────────────
function drawCircle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
) {
  ctx.arc(x, y, r, 0, Math.PI * 2);
}

function drawPentagon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
) {
  for (let i = 0; i < 5; i++) {
    const a = ((2 * Math.PI) / 5) * i - Math.PI / 2;
    const px = x + r * Math.cos(a);
    const py = y + r * Math.sin(a);
    if (i === 0) {
      ctx.moveTo(px, py);
    } else {
      ctx.lineTo(px, py);
    }
  }
  ctx.closePath();
}

function drawDiamond(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
) {
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r, y);
  ctx.closePath();
}

function drawTriangle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
) {
  const half = r * 0.866;
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + half, y + r * 0.5);
  ctx.lineTo(x - half, y + r * 0.5);
  ctx.closePath();
}

function nodeRadius(entityType: string, globalScale: number): number {
  const px = entityType === 'File' ? 13 : 10;
  return Math.min(40, Math.max(2, px / globalScale));
}

function drawLabel(
  ctx: CanvasRenderingContext2D,
  wx: number,
  wy: number,
  worldR: number,
  globalScale: number,
  text: string,
) {
  if (globalScale < 0.4) return;

  const t = ctx.getTransform();
  const sx = wx * t.a + t.e;
  const sy = wy * t.d + t.f;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  const FONT_PX = 10;
  ctx.font = `${FONT_PX}px Inter, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  const maxCh = 24;
  const label = text.length > maxCh ? `${text.slice(0, maxCh)}…` : text;
  const tw = ctx.measureText(label).width;
  const labelY = sy + worldR * globalScale + 3;

  ctx.fillStyle = 'rgba(10,15,30,0.72)';
  ctx.beginPath();
  ctx.roundRect(sx - tw / 2 - 3, labelY - 1, tw + 6, FONT_PX + 3, 3);
  ctx.fill();

  ctx.fillStyle = '#e2e8f0';
  ctx.fillText(label, sx, labelY);

  ctx.restore();
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  targetR: number,
  color: string,
  globalScale: number,
) {
  const dx = tx - sx;
  const dy = ty - sy;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist === 0) return;

  const ux = dx / dist;
  const uy = dy / dist;
  const tipX = tx - ux * targetR;
  const tipY = ty - uy * targetR;
  const arrowLen = 7 / globalScale;
  const arrowWidth = Math.PI / 7;

  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(
    tipX - arrowLen * Math.cos(-arrowWidth + Math.atan2(dy, dx)),
    tipY - arrowLen * Math.sin(-arrowWidth + Math.atan2(dy, dx)),
  );
  ctx.lineTo(
    tipX - arrowLen * Math.cos(arrowWidth + Math.atan2(dy, dx)),
    tipY - arrowLen * Math.sin(arrowWidth + Math.atan2(dy, dx)),
  );
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

type FileSearchHit = {
  nodeId: string;
  filePath: string;
  label: string;
};

function filePathMatchesQuery(filePath: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q || !filePath) return false;
  const base = filePath.split('/').pop()?.toLowerCase() ?? '';
  const full = filePath.toLowerCase();
  return full.includes(q) || base.includes(q);
}

function buildFileSearchHits(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nodes: any[],
  query: string,
  limit = 20,
): FileSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const seenPaths = new Set<string>();
  const hits: FileSearchHit[] = [];

  for (const n of nodes) {
    if (String(n.entityType ?? n.entity_type ?? '') !== 'File') continue;
    const filePath = String(n.source_file ?? '');
    if (!filePath || seenPaths.has(filePath)) continue;
    if (!filePathMatchesQuery(filePath, q)) continue;
    seenPaths.add(filePath);
    hits.push({
      nodeId: String(n.id),
      filePath,
      label: filePath.split('/').pop() || filePath,
    });
  }

  return hits
    .sort((a, b) => a.filePath.localeCompare(b.filePath))
    .slice(0, limit);
}

function buildExactFileMatchingNodeIds(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nodes: any[],
  filePath: string,
): Set<string> {
  if (!filePath) return new Set();
  const matched = new Set<string>();
  for (const n of nodes) {
    if (String(n.source_file ?? '') === filePath) {
      matched.add(String(n.id));
    }
  }
  return matched;
}

function buildFileMatchingNodeIds(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nodes: any[],
  query: string,
): Set<string> {
  const q = query.trim().toLowerCase();
  if (!q) return new Set();

  const matchingPaths = new Set<string>();
  for (const n of nodes) {
    if (String(n.entityType ?? n.entity_type ?? '') !== 'File') continue;
    const filePath = String(n.source_file ?? '');
    if (filePath && filePathMatchesQuery(filePath, q)) {
      matchingPaths.add(filePath);
    }
  }

  const matched = new Set<string>();
  for (const n of nodes) {
    const filePath = String(n.source_file ?? '');
    if (matchingPaths.has(filePath)) {
      matched.add(String(n.id));
    }
  }
  return matched;
}

function linkEndpointId(endpoint: unknown): string {
  if (endpoint != null && typeof endpoint === 'object' && 'id' in endpoint) {
    return String((endpoint as { id: unknown }).id ?? '');
  }
  return String(endpoint ?? '');
}

function buildGraphAdjacency(
  edges: Array<{ source: unknown; target: unknown }>,
): Map<string, Set<string>> {
  const adjacency = new Map<string, Set<string>>();
  const connect = (a: string, b: string) => {
    if (!a || !b || a === b) return;
    if (!adjacency.has(a)) adjacency.set(a, new Set());
    if (!adjacency.has(b)) adjacency.set(b, new Set());
    adjacency.get(a)!.add(b);
    adjacency.get(b)!.add(a);
  };
  for (const edge of edges) {
    connect(linkEndpointId(edge.source), linkEndpointId(edge.target));
  }
  return adjacency;
}

function buildFocusNeighborhood(
  focusNodeId: string,
  adjacency: Map<string, Set<string>>,
): Set<string> {
  const ids = new Set<string>([focusNodeId]);
  const neighbors = adjacency.get(focusNodeId);
  if (neighbors) neighbors.forEach(id => ids.add(id));
  return ids;
}

// ─── component ────────────────────────────────────────────────────────────────
const CallGraphVisualization = ({
  scanId,
  repoUrl,
  branchName,
  patToken,
  onBack,
  embedded = false,
  graphPayload: externalPayload,
  graphLoading: externalLoading,
  graphError: externalGraphError,
}: Props) => {
  const [fileDetail, setFileDetail] = useState<FileDetailState | null>(null);
  const [selectedEntityRowId, setSelectedEntityRowId] = useState<string | null>(
    null,
  );
  const [fileError, setFileError] = useState<string | null>(null);
  const [graphSearch, setGraphSearch] = useState('');
  const [pinnedFilePath, setPinnedFilePath] = useState<string | null>(null);
  const [focusedSearchNodeId, setFocusedSearchNodeId] = useState<string | null>(
    null,
  );
  const [focusNodeId, setFocusNodeId] = useState<string | null>(null);
  const [blastRadiusDepth, setBlastRadiusDepth] = useState(1);
  const [blastRadiusSeedEntityKey, setBlastRadiusSeedEntityKey] = useState<
    string | null
  >(null);
  const [riskHeatmapEnabled, setRiskHeatmapEnabled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const graphRef = useRef<unknown>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const useInternalFetch = externalPayload === undefined;

  const internalQuery = useCallGraph(scanId, useInternalFetch);
  const payload = useInternalFetch ? internalQuery.data : externalPayload;
  const loading = useInternalFetch
    ? internalQuery.isPending
    : (externalLoading ?? false);
  const graphLoadError = useInternalFetch
    ? internalQuery.isError
    : Boolean(externalGraphError);
  const graphError = useInternalFetch
    ? internalQuery.error
    : externalGraphError;

  const entitiesByFile = payload?.entitiesByFile ?? {};

  const entitiesByKey = useMemo(
    () => buildEntitiesByKey(entitiesByFile),
    [entitiesByFile],
  );

  const entityKeyToNodeIds = useMemo(
    () => buildEntityKeyToNodeIds(payload?.nodes ?? [], entitiesByKey),
    [payload?.nodes, entitiesByKey],
  );

  const filePathToNodeId = useMemo(
    () => buildFilePathToNodeId(payload?.nodes ?? []),
    [payload?.nodes],
  );

  useEffect(() => {
    setBlastRadiusDepth(1);
  }, [blastRadiusSeedEntityKey]);

  const blastRadiusResult = useMemo(() => {
    if (!blastRadiusSeedEntityKey) return null;
    return computeBlastRadius(
      blastRadiusSeedEntityKey,
      blastRadiusDepth,
      entitiesByKey,
      entityKeyToNodeIds,
      filePathToNodeId,
    );
  }, [
    blastRadiusDepth,
    blastRadiusSeedEntityKey,
    entitiesByKey,
    entityKeyToNodeIds,
    filePathToNodeId,
  ]);

  const fileCallables = useMemo(() => {
    if (!fileDetail?.filePath) return [];
    const list = entitiesByFile[fileDetail.filePath] ?? [];
    return [...list].sort((a, b) => entityScore(b) - entityScore(a));
  }, [entitiesByFile, fileDetail?.filePath]);

  const selectCallableForBlastRadius = useCallback(
    (ent: ScanCallableEntity, rowId: string) => {
      setSelectedEntityRowId(rowId || null);
      const entityKey = (ent.entityKey ?? '').trim();
      if (entityKey) {
        setBlastRadiusSeedEntityKey(entityKey);
        const nodeIds = entityKeyToNodeIds.get(entityKey);
        if (nodeIds?.length) {
          setFocusNodeId(nodeIds[0]);
        }
      } else {
        setBlastRadiusSeedEntityKey(null);
      }
    },
    [entityKeyToNodeIds],
  );

  const fetchFileContent = useCallback(
    async (filePath: string) => {
      if (!patToken?.trim()) {
        setFileError('PAT is missing. Re-select the component from the list.');
        setFileDetail({ filePath, content: '', loadingCode: false });
        return;
      }
      if (!repoUrl?.trim() || !branchName?.trim()) {
        setFileError('Repository URL or branch is missing for this scan.');
        setFileDetail({ filePath, content: '', loadingCode: false });
        return;
      }

      setFileError(null);
      setFileDetail({ filePath, content: '', loadingCode: true });
      setSelectedEntityRowId(null);

      try {
        const res = await getFileContent({
          repoUrl,
          branchName,
          filePath,
          scanId: scanId.trim(),
          pat: patToken,
        });
        setFileDetail({
          filePath,
          content: res.data?.content ?? '',
          language: res.data?.language,
          loadingCode: false,
        });
      } catch (err) {
        setFileError(getApiErrorMessage(err, 'Failed to load file content'));
        setFileDetail({ filePath, content: '', loadingCode: false });
      }
    },
    [branchName, patToken, repoUrl, scanId],
  );

  const openFile = useCallback(
    (filePath: string, entityRowId?: string) => {
      void fetchFileContent(filePath);
      if (entityRowId) setSelectedEntityRowId(entityRowId);
    },
    [fetchFileContent],
  );

  const handleNodeClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (node: any) => {
      const nodeId = String(node.id ?? '');
      const entityType = String(node.entityType ?? node.entity_type ?? '');

      if (entityType === 'File') {
        setBlastRadiusSeedEntityKey(null);
        setFocusNodeId(prev => (prev === nodeId ? null : nodeId));
        const filePath = String(node.source_file ?? '');
        if (filePath) openFile(filePath);
        return;
      }

      if (['Function', 'Method', 'Constructor'].includes(entityType)) {
        const entityKey = String(node.entity_key ?? '').trim();
        if (entityKey) {
          setBlastRadiusSeedEntityKey(entityKey);
        }
        setFocusNodeId(nodeId);
        const filePath = String(node.source_file ?? '');
        const rowId = String(node.entity_row_id ?? '');
        if (filePath) openFile(filePath, rowId || undefined);
      }
    },
    [openFile],
  );

  const graphData = useMemo(() => {
    if (!payload?.nodes?.length)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return { nodes: [] as any[], links: [] as any[] };

    const nodes = payload.nodes.map(n => {
      const entityType = String(n.entity_type ?? '');
      return {
        ...n,
        entityType,
        color: TYPE_COLOR[entityType] ?? FALLBACK_COLOR,
      };
    });

    const links = (payload.edges ?? []).map(e => ({
      source: String(e.source),
      target: String(e.target),
      relation: e.relation ?? '',
      context: e.context ?? '',
    }));

    return { nodes, links };
  }, [payload]);

  const graphAdjacency = useMemo(
    () => buildGraphAdjacency(payload?.edges ?? []),
    [payload?.edges],
  );

  const riskMetricsByNodeId = useMemo(
    () =>
      computeRiskHeatmap(
        graphData.nodes,
        payload?.edges ?? [],
        entitiesByFile,
      ),
    [graphData.nodes, payload?.edges, entitiesByFile],
  );

  const focusNeighborhood = useMemo(() => {
    if (!focusNodeId) return null;
    return buildFocusNeighborhood(focusNodeId, graphAdjacency);
  }, [focusNodeId, graphAdjacency]);

  const highlightNodeIds = useMemo(() => {
    if (blastRadiusResult?.nodeIds.size) {
      return blastRadiusResult.nodeIds;
    }
    if (focusNodeId && focusNeighborhood) {
      return focusNeighborhood;
    }
    return null;
  }, [blastRadiusResult, focusNodeId, focusNeighborhood]);

  const highlightCenterNodeId = useMemo(() => {
    if (blastRadiusResult?.seedNodeIds.length) {
      return blastRadiusResult.seedNodeIds[0] ?? null;
    }
    return focusNodeId;
  }, [blastRadiusResult, focusNodeId]);

  const focusCenterLabel = useMemo(() => {
    if (!highlightCenterNodeId) return null;
    const node = graphData.nodes.find(
      n => String(n.id) === highlightCenterNodeId,
    );
    return node ? String(node.label ?? node.id) : highlightCenterNodeId;
  }, [highlightCenterNodeId, graphData.nodes]);

  const searchQuery = graphSearch.trim();
  const searchActive = searchQuery.length > 0;

  const searchResults = useMemo(
    () => buildFileSearchHits(graphData.nodes, searchQuery),
    [graphData.nodes, searchQuery],
  );

  const matchingNodeIds = useMemo(() => {
    if (pinnedFilePath) {
      return buildExactFileMatchingNodeIds(graphData.nodes, pinnedFilePath);
    }
    return buildFileMatchingNodeIds(graphData.nodes, searchQuery);
  }, [graphData.nodes, pinnedFilePath, searchQuery]);

  useEffect(() => {
    if (!searchOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!searchRef.current?.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [searchOpen]);

  const focusGraphNode = useCallback((nodeId: string) => {
    setFocusedSearchNodeId(nodeId);
    setFocusNodeId(nodeId);
    requestAnimationFrame(() => {
      const g = graphRef.current as {
        graphData?: () => {
          nodes: { id: string; x?: number; y?: number }[];
        };
        centerAt?: (x: number, y: number, ms?: number) => void;
        zoom?: (k: number, ms?: number) => void;
      } | null;
      const node = g?.graphData?.()?.nodes?.find(n => String(n.id) === nodeId);
      if (node?.x != null && node?.y != null) {
        g?.centerAt?.(node.x, node.y, 600);
        g?.zoom?.(2.8, 600);
      }
    });
  }, []);

  const handleSearchResultSelect = useCallback(
    (hit: FileSearchHit) => {
      setGraphSearch(hit.label);
      setPinnedFilePath(hit.filePath);
      setSearchOpen(false);
      focusGraphNode(hit.nodeId);
      openFile(hit.filePath);
    },
    [focusGraphNode, openFile],
  );

  const clearGraphSearch = useCallback(() => {
    setGraphSearch('');
    setPinnedFilePath(null);
    setFocusedSearchNodeId(null);
    setFocusNodeId(null);
    setSearchOpen(false);
  }, []);

  const paintNode = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      node: any,
      ctx: CanvasRenderingContext2D,
      globalScale: number,
    ) => {
      if (node.x == null || node.y == null) return;

      const nodeId = String(node.id ?? '');
      const isMatch = !searchActive || matchingNodeIds.has(nodeId);
      const isSearchFocused = focusedSearchNodeId === nodeId;
      const isFocusCenter = highlightCenterNodeId === nodeId;
      const inHighlight =
        !highlightNodeIds || highlightNodeIds.has(nodeId);
      const isSelected =
        selectedEntityRowId &&
        String(node.entity_row_id ?? '') === selectedEntityRowId;

      const { x, y, entityType, color } = node;
      const label = String(node.label ?? node.id ?? '');
      const r = nodeRadius(entityType, globalScale);
      const riskMetrics = riskMetricsByNodeId.get(nodeId);
      const fillColor =
        riskHeatmapEnabled && riskMetrics
          ? riskMetrics.riskColor
          : (color as string);

      let nodeAlpha = 1;
      if (highlightNodeIds) {
        nodeAlpha = inHighlight ? 1 : 0.07;
      }
      if (searchActive && !matchingNodeIds.has(nodeId)) {
        nodeAlpha = Math.min(nodeAlpha, 0.1);
      }

      const tracePath = () => {
        ctx.beginPath();
        switch (entityType) {
          case 'File':
            drawCircle(ctx, x, y, r);
            break;
          case 'Function':
            drawPentagon(ctx, x, y, r);
            break;
          case 'Method':
            drawDiamond(ctx, x, y, r);
            break;
          case 'Constructor':
            drawTriangle(ctx, x, y, r);
            break;
          default:
            drawCircle(ctx, x, y, r * 0.8);
            break;
        }
      };

      ctx.save();
      ctx.globalAlpha = nodeAlpha;

      tracePath();
      ctx.fillStyle = fillColor;
      ctx.fill();

      if (isFocusCenter) {
        tracePath();
        ctx.strokeStyle = '#FBBF24';
        ctx.lineWidth = 3 / globalScale;
        ctx.stroke();
      } else if (isSelected || isSearchFocused) {
        tracePath();
        ctx.strokeStyle = isSearchFocused ? '#FBBF24' : '#fff';
        ctx.lineWidth = (isSearchFocused ? 2.5 : 2) / globalScale;
        ctx.stroke();
      } else if (highlightNodeIds && inHighlight && !isFocusCenter) {
        tracePath();
        ctx.strokeStyle = blastRadiusResult
          ? 'rgba(251,191,36,0.55)'
          : 'rgba(255,255,255,0.4)';
        ctx.lineWidth = (blastRadiusResult ? 1.5 : 1.25) / globalScale;
        ctx.stroke();
      } else if (isMatch && searchActive) {
        tracePath();
        ctx.strokeStyle = 'rgba(251,191,36,0.55)';
        ctx.lineWidth = 1.5 / globalScale;
        ctx.stroke();
      }

      tracePath();
      const grad = ctx.createRadialGradient(
        x - r * 0.3,
        y - r * 0.35,
        r * 0.05,
        x,
        y,
        r,
      );
      grad.addColorStop(0, 'rgba(255,255,255,0.45)');
      grad.addColorStop(0.45, 'rgba(255,255,255,0.10)');
      grad.addColorStop(1, 'rgba(0,0,0,0.25)');
      ctx.fillStyle = grad;
      ctx.fill();

      if (
        (highlightNodeIds ? inHighlight : isMatch) &&
        nodeAlpha >= 0.5
      ) {
        drawLabel(ctx, x, y, r, globalScale, label);
      }

      ctx.restore();
    },
    [
      blastRadiusResult,
      highlightCenterNodeId,
      highlightNodeIds,
      focusedSearchNodeId,
      matchingNodeIds,
      riskHeatmapEnabled,
      riskMetricsByNodeId,
      searchActive,
      selectedEntityRowId,
    ],
  );

  const paintLink = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      link: any,
      ctx: CanvasRenderingContext2D,
      globalScale: number,
    ) => {
      const src = link.source;
      const tgt = link.target;
      if (src?.x == null || tgt?.x == null) return;

      const srcId = linkEndpointId(src);
      const tgtId = linkEndpointId(tgt);
      const inBlastRadius =
        blastRadiusResult &&
        highlightNodeIds?.has(srcId) &&
        highlightNodeIds.has(tgtId);
      const isFocusLink =
        !highlightCenterNodeId ||
        srcId === highlightCenterNodeId ||
        tgtId === highlightCenterNodeId;
      const linkVisible =
        !searchActive ||
        matchingNodeIds.has(srcId) ||
        matchingNodeIds.has(tgtId);

      const relation = String(link.relation ?? '');
      const isCalls = relation === 'calls';
      const isApiCalls = relation === 'api_calls';
      const edgeColor = isApiCalls
        ? EDGE_COLOR.api_calls
        : isCalls
          ? EDGE_COLOR.calls
          : EDGE_COLOR.contains;
      const baseAlpha = linkVisible
        ? isApiCalls
          ? 0.95
          : isCalls
            ? 0.9
            : 0.65
        : 0.05;
      let edgeAlpha = baseAlpha;
      if (blastRadiusResult && highlightNodeIds) {
        if (isCalls && inBlastRadius) {
          edgeAlpha = baseAlpha;
        } else if (inBlastRadius) {
          edgeAlpha = Math.min(baseAlpha, 0.45);
        } else {
          edgeAlpha = 0.04;
        }
      } else if (highlightCenterNodeId) {
        edgeAlpha = isFocusLink ? baseAlpha : 0.04;
      }
      const edgeWidth = isApiCalls ? 2 : isCalls ? 1.5 : 1.2;
      const showArrow = linkVisible && edgeAlpha >= 0.2;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
      ctx.strokeStyle = edgeColor;
      ctx.globalAlpha = edgeAlpha;
      ctx.lineWidth =
        (highlightCenterNodeId && isFocusLink ? edgeWidth * 1.35 : edgeWidth) /
        globalScale;
      if (isApiCalls) {
        ctx.setLineDash([5 / globalScale, 4 / globalScale]);
      } else {
        ctx.setLineDash([]);
      }
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tgtEntityType = (tgt as any).entityType ?? '';
      const tgtR = nodeRadius(tgtEntityType, globalScale);

      if (!showArrow) return;

      ctx.save();
      ctx.globalAlpha = edgeAlpha;
      drawArrow(ctx, src.x, src.y, tgt.x, tgt.y, tgtR, edgeColor, globalScale);
      ctx.restore();
    },
    [
      blastRadiusResult,
      highlightCenterNodeId,
      highlightNodeIds,
      matchingNodeIds,
      searchActive,
    ],
  );

  const stats = payload?.stats;
  const LEGEND = [
    { color: TYPE_COLOR.File, symbol: '⬤', label: 'File' },
    { color: TYPE_COLOR.Function, symbol: '⬟', label: 'Function' },
    { color: TYPE_COLOR.Method, symbol: '◆', label: 'Method' },
  ];

  const closePanel = () => {
    setFileDetail(null);
    setSelectedEntityRowId(null);
    setBlastRadiusSeedEntityKey(null);
    setFocusNodeId(null);
  };

  return (
    <div className={callGraphRootClass(embedded)}>
      {!embedded && (
        <div className="mb-4 px-1">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="m-0 text-xl font-semibold text-[var(--inv-text)]">
                Call Graph
              </h1>
              <p className="m-0 mt-1 text-sm text-[var(--inv-text-secondary)]">
                Scan <code className="text-[0.85em]">{scanId}</code>
              </p>
            </div>
            {onBack && (
              <Button variant="secondary" onClick={onBack}>
                Back
              </Button>
            )}
          </div>
        </div>
      )}

      {loading && <CallGraphVisualizationSkeleton embedded={embedded} />}

      {!loading && graphLoadError && (
        <div className="m-4 rounded-[14px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] p-6">
          <p className="m-0 text-sm text-[#b91c1c]">
            {getApiErrorMessage(graphError, 'Failed to load call graph')}
          </p>
        </div>
      )}

      {!loading && payload?.message && graphData.nodes.length > 0 && (
        <div className="mx-3 mt-3 rounded-[10px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-3 py-2">
          <p className="m-0 text-sm text-[var(--inv-text-secondary)]">
            {payload.message}
          </p>
        </div>
      )}

      {!loading && payload && graphData.nodes.length === 0 && (
        <div className="m-4 rounded-[14px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] p-6">
          <p className="m-0 text-sm text-[var(--inv-text-secondary)]">
            No graph nodes were returned for this scan.
          </p>
        </div>
      )}

      {!loading && graphData.nodes.length > 0 && stats && (
        <div className={callGraphToolbarClass}>
          <span className={callGraphStatChipClass}>
            <span className={callGraphStatLabelClass}>Nodes</span>
            <span className={callGraphStatValueClass}>
              {stats.nodeCount ?? 0}
            </span>
          </span>
          <span className={callGraphStatChipClass}>
            <span className={callGraphStatLabelClass}>Edges</span>
            <span className={callGraphStatValueClass}>
              {stats.edgeCount ?? 0}
            </span>
          </span>
          <span className={callGraphStatChipClass}>
            <span className={callGraphStatLabelClass}>Calls</span>
            <span className={callGraphStatValueClass}>
              {stats.callsEdgeCount ?? 0}
            </span>
          </span>
          <span className={callGraphStatChipClass}>
            <span className={callGraphStatLabelClass}>API links</span>
            <span className={callGraphStatValueClass}>
              {stats.apiCallsEdgeCount ?? 0}
            </span>
          </span>
          <p className={callGraphToolbarHintClass}>
            Click a node to inspect a file
          </p>
        </div>
      )}

      {!loading && graphData.nodes.length > 0 && (
        <div className={callGraphLayoutClass}>
          <div className={callGraphCanvasClass(Boolean(fileDetail))}>
            <div className={callGraphLegendClass}>
              <button
                type="button"
                className={`mb-1 flex h-10 w-full cursor-pointer items-center justify-center rounded-md border px-2 text-center text-[0.68rem] font-semibold transition-colors ${
                  riskHeatmapEnabled
                    ? 'border-amber-300 bg-amber-400 text-slate-950 hover:bg-amber-300 hover:text-slate-950'
                    : 'border-white/20 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white'
                }`}
                onClick={() => setRiskHeatmapEnabled(prev => !prev)}
                title="Color nodes by in-degree, out-degree, betweenness, and call frequency"
                aria-pressed={riskHeatmapEnabled}>
                {riskHeatmapEnabled ? 'Risk heatmap on' : 'Risk heatmap'}
              </button>
              {riskHeatmapEnabled ? (
                <div className="mb-1 flex flex-col gap-1">
                  <p className="m-0 text-[0.62rem] font-semibold uppercase tracking-wide text-slate-400">
                    Hotspot risk
                  </p>
                  {RISK_HEATMAP_LEGEND.map(item => (
                    <div
                      key={item.label}
                      className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-[0.68rem] text-slate-200">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {LEGEND.map(item => (
                    <div
                      key={item.label}
                      className="flex items-center gap-2">
                      <span
                        className="text-[15px] leading-none"
                        style={{ color: item.color }}>
                        {item.symbol}
                      </span>
                      <span className="text-xs font-medium text-slate-100">
                        {item.label}
                      </span>
                    </div>
                  ))}
                  <div className="mt-2 border-t border-white/15 pt-2">
                    <p className="m-0 mb-1.5 text-[0.62rem] font-semibold uppercase tracking-wide text-slate-400">
                      Edges
                    </p>
                    {EDGE_LEGEND.map(item => (
                      <div
                        key={item.label}
                        className="mb-1 flex items-center gap-2 last:mb-0">
                        <span
                          className="inline-block h-0.5 w-4 shrink-0"
                          style={{
                            backgroundColor: item.color,
                            backgroundImage: item.dashed
                              ? `repeating-linear-gradient(90deg, ${item.color} 0 3px, transparent 3px 5px)`
                              : undefined,
                          }}
                        />
                        <span className="text-[0.68rem] text-slate-200">
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {(highlightCenterNodeId || blastRadiusResult) && (
                <div className="mt-1.5 border-t border-white/15 pt-2">
                  <p className="m-0 text-[0.65rem] font-semibold uppercase tracking-wide text-amber-300/90">
                    {blastRadiusResult ? 'Blast radius' : 'Focus'}
                  </p>
                  <p
                    className="m-0 mt-0.5 max-w-[140px] truncate text-[0.7rem] text-slate-200"
                    title={focusCenterLabel ?? undefined}>
                    {focusCenterLabel}
                  </p>
                  <p className="m-0 mt-1 text-[0.62rem] leading-snug text-slate-400">
                    {blastRadiusResult
                      ? `Depth ${blastRadiusResult.depth} / ${blastRadiusResult.maxDepth}: ${blastRadiusResult.totalAffected} affected`
                      : highlightNodeIds
                        ? `${highlightNodeIds.size - 1} connected`
                        : '0 connected'}
                  </p>
                  <button
                    type="button"
                    className="mt-1.5 flex h-10 w-full cursor-pointer items-center justify-center rounded-md border border-amber-300 bg-amber-400 px-2 text-center text-[0.68rem] font-semibold text-slate-950 transition-colors hover:bg-amber-300 hover:text-slate-950"
                    onClick={() => {
                      setFocusNodeId(null);
                      setBlastRadiusSeedEntityKey(null);
                    }}>
                    Clear focus
                  </button>
                </div>
              )}
            </div>

            <div ref={searchRef} className={callGraphSearchWrapClass}>
              <div className={callGraphSearchFieldClass}>
                <Input
                  type="text"
                  className={callGraphSearchInputClass}
                  placeholder="Search files…"
                  value={graphSearch}
                  onChange={e => {
                    setGraphSearch(e.target.value);
                    setPinnedFilePath(null);
                    setSearchOpen(true);
                    setFocusedSearchNodeId(null);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  aria-label="Search files in graph"
                />
                {searchActive && (
                  <Button
                    type="button"
                    variant="ghost"
                    className={callGraphSearchClearClass}
                    onClick={clearGraphSearch}
                    aria-label="Clear search">
                    ×
                  </Button>
                )}
              </div>
              {searchActive && searchOpen && (
                <div className={callGraphSearchResultsClass}>
                  {searchResults.length === 0 ? (
                    <p className={callGraphSearchEmptyClass}>
                      No matching files
                    </p>
                  ) : (
                    <>
                      <p className={callGraphSearchCountClass}>
                        {searchResults.length} file
                        {searchResults.length === 1 ? '' : 's'}
                      </p>
                      <ul className={callGraphSearchListClass}>
                        {searchResults.map(hit => (
                          <li key={hit.nodeId}>
                            <Button
                              type="button"
                              variant="ghost"
                              className={callGraphSearchItemClass(
                                focusedSearchNodeId === hit.nodeId,
                              )}
                              onClick={() => handleSearchResultSelect(hit)}>
                              <span className={callGraphSearchItemLabelClass}>
                                {hit.label}
                              </span>
                              <span className={callGraphSearchItemPathClass}>
                                {hit.filePath}
                              </span>
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>

            <ForceGraph2D
              ref={graphRef}
              graphData={graphData}
              backgroundColor="#111827"
              nodeCanvasObject={paintNode}
              nodeCanvasObjectMode={() => 'replace'}
              linkCanvasObject={paintLink}
              linkCanvasObjectMode={() => 'replace'}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              linkLabel={(link: any) => {
                const relation = String(link.relation ?? '');
                if (relation === 'api_calls') {
                  const context = String(link.context ?? '').trim();
                  return context ? `API: ${context}` : 'API call (FE→BE)';
                }
                if (relation === 'calls') return 'Call';
                if (relation === 'contains') return 'Contains';
                return relation || 'Edge';
              }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              nodeLabel={(n: any) => {
                const base = String(n.label ?? n.id ?? '');
                if (!riskHeatmapEnabled) return base;
                const metrics = riskMetricsByNodeId.get(String(n.id ?? ''));
                if (!metrics) return base;
                return `${base} · risk ${Math.round(metrics.riskScore * 100)}`;
              }}
              onNodeClick={handleNodeClick}
              onBackgroundClick={() => setFocusNodeId(null)}
              d3AlphaDecay={0.03}
              d3VelocityDecay={0.4}
              warmupTicks={200}
              cooldownTicks={0}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              d3Force={(d3forces: any) => {
                d3forces('charge')
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ?.strength((n: any) => (n.entityType === 'File' ? -180 : -50))
                  .distanceMax(250);
                d3forces('link')
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ?.distance((l: any) => {
                    if (l.relation === 'api_calls') return 72;
                    if (l.relation === 'calls') return 55;
                    return 28;
                  })
                  .strength(1);
                d3forces('collision')
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ?.radius((n: any) => (n.entityType === 'File' ? 20 : 12))
                  .strength(0.9);
              }}
              onEngineStop={() => {
                const g = graphRef.current as {
                  zoomToFit?: (ms?: number, pad?: number) => void;
                } | null;
                g?.zoomToFit?.(400, 24);
              }}
            />
          </div>

          {fileDetail && (
            <div className={callGraphDetailsPanelClass}>
              <div className={callGraphDetailsHeaderClass}>
                <div className="min-w-0">
                  <h3 className="m-0 truncate text-sm font-semibold text-[var(--inv-text)]">
                    {fileDetail.filePath.split('/').pop()}
                  </h3>
                  <p
                    className="m-0 mt-1 truncate font-mono text-[0.7rem] text-[var(--inv-text-muted)]"
                    title={fileDetail.filePath}>
                    {fileDetail.filePath}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 shrink-0 p-0"
                  onClick={closePanel}
                  aria-label="Close file details">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className={callGraphDetailsBodyClass}>
                <div className={callGraphCallablesSectionClass}>
                  <h4 className="m-0 mb-2 text-[0.75rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
                    Callables ({fileCallables.length})
                  </h4>
                  {fileCallables.length === 0 ? (
                    <p className="m-0 text-[0.8rem] text-[var(--inv-text-muted)]">
                      No callable entities for this file.
                    </p>
                  ) : (
                    <ul className="m-0 flex list-none flex-col gap-2 p-0">
                      {fileCallables.map(ent => {
                        const rowId = getEntityRowId(ent);
                        const key = rowId || `${ent.name}-${ent.lineStart}`;
                        const fanIns = fanInList(ent);
                        const fanouts = resolvedFanoutList(ent);
                        const selected = selectedEntityRowId === rowId;
                        const showBlastRadius =
                          selected &&
                          blastRadiusResult &&
                          blastRadiusSeedEntityKey === (ent.entityKey ?? '');
                        return (
                          <li key={key}>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() =>
                                selectCallableForBlastRadius(ent, rowId)
                              }
                              className={callGraphCallableCardClass(selected)}>
                              <div className="flex min-w-0 items-start justify-between gap-2">
                                <span className="min-w-0 break-all text-[0.8rem] font-semibold leading-snug text-[var(--inv-text)]">
                                  {formatEntityCallableLabel(
                                    ent,
                                    ent.entityKey ?? key,
                                  )}
                                </span>
                                <span className="shrink-0 rounded-md bg-[var(--inv-surface)] px-1.5 py-0.5 font-mono text-[0.65rem] text-[var(--inv-text-muted)]">
                                  L{ent.lineStart}–{ent.lineEnd}
                                </span>
                              </div>
                              <p className="m-0 text-[0.7rem] text-[var(--inv-text-secondary)]">
                                {ent.type}
                              </p>
                              <EntityScoresDisplay
                                scores={ent.metadata?.scores}
                              />
                              {showBlastRadius ? (
                                <BlastRadiusControls
                                  result={blastRadiusResult}
                                  depth={blastRadiusDepth}
                                  maxDepth={blastRadiusResult.maxDepth}
                                  maxFanInDepth={blastRadiusResult.maxFanInDepth}
                                  maxFanOutDepth={blastRadiusResult.maxFanOutDepth}
                                  onDepthChange={setBlastRadiusDepth}
                                  onClear={() => {
                                    setBlastRadiusSeedEntityKey(null);
                                    setFocusNodeId(null);
                                  }}
                                />
                              ) : null}
                              <EntityKeyListDisplay
                                label={
                                  blastRadiusDepth > 1 && selected
                                    ? 'Called by (direct)'
                                    : 'Called by'
                                }
                                keys={fanIns}
                                entitiesByKey={entitiesByKey}
                              />
                              <EntityKeyListDisplay
                                label={
                                  blastRadiusDepth > 1 && selected
                                    ? 'Calls (direct)'
                                    : 'Calls'
                                }
                                keys={fanouts}
                                entitiesByKey={entitiesByKey}
                              />
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div className={callGraphSourceSectionClass}>
                  <h4 className={callGraphSourceTitleClass}>
                    Source
                    {fileDetail.language ? ` (${fileDetail.language})` : ''}
                  </h4>
                  {fileError && (
                    <p className="m-0 mb-2 shrink-0 text-[0.85rem] text-[#b91c1c]">
                      {fileError}
                    </p>
                  )}
                  {fileDetail.loadingCode ? (
                    <CallGraphSourceSkeleton />
                  ) : fileDetail.content ? (
                    <div className={callGraphSourceScrollClass}>
                      <CodeEditor
                        instanceId={`call-graph-${fileDetail.filePath}`}
                        codeFromApi={fileDetail.content}
                        language={fileDetail.language ?? fileDetail.filePath}
                        height="100%"
                      />
                    </div>
                  ) : (
                    <p className="m-0 text-[0.85rem] text-slate-500">
                      No source content returned.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CallGraphVisualization;
